// useSlipRouting — wraps the route_slip(legs, user_id, region) RPC.
//
// The route engine (Phase 2 P2-3/P2-4) ranks bookmakers per leg by:
//   • user's primary / has-account flags (if user_id given)
//   • current odds_decimal (when bookmaker_odds_quotes have a row)
//   • is_sharp + payout_speed
//   • baseline market margin
// and returns the top recommendation + up to 4 alternatives per leg.
//
// This hook caches the response per-(legs key, user, region) and
// exposes a per-leg lookup helper so leg-row components can render a
// RouteBadge inline without each row firing its own request.

import { useCallback, useMemo } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { useRpcQuery } from "../data/useRpcQuery.ts";

export interface RouteLegInput {
  fixture_id: string;
  market: string;
  selection: string;
}

export interface RouteRecommendation {
  slug: string;
  display_name: string;
  region: string;
  is_sharp: boolean;
  market_strengths?: string[] | null;
  odds_decimal?: number | null;
  observed_at?: string | null;
  source?: string | null;
  baseline_margin?: number | null;
  payout_speed?: string | null;
  user_has_account?: boolean | null;
  is_user_primary?: boolean | null;
  route_score?: number | null;
}

export interface RouteEntry {
  fixture_id: string;
  market: string;
  selection: string;
  recommended: RouteRecommendation | null;
  alternatives: RouteRecommendation[];
  considered_count: number;
}

export interface SlipRoutingState {
  routes: RouteEntry[];
  loading: boolean;
  error: string | null;
  // Lookup helper keyed by `${fixture_id}|${market}|${selection}`.
  lookup: (fixtureId: string, market: string, selection: string) => RouteEntry | null;
  refetch: () => Promise<void>;
}

function normRecommendation(raw: unknown): RouteRecommendation | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.slug !== "string") return null;
  return {
    slug: r.slug,
    display_name: typeof r.display_name === "string" ? r.display_name : r.slug,
    region: typeof r.region === "string" ? r.region : "GLOBAL",
    is_sharp: r.is_sharp === true,
    market_strengths: Array.isArray(r.market_strengths) ? (r.market_strengths as string[]) : null,
    odds_decimal: typeof r.odds_decimal === "number" ? r.odds_decimal : null,
    observed_at: typeof r.observed_at === "string" ? r.observed_at : null,
    source: typeof r.source === "string" ? r.source : null,
    baseline_margin: typeof r.baseline_margin === "number" ? r.baseline_margin : null,
    payout_speed: typeof r.payout_speed === "string" ? r.payout_speed : null,
    user_has_account: typeof r.user_has_account === "boolean" ? r.user_has_account : null,
    is_user_primary: typeof r.is_user_primary === "boolean" ? r.is_user_primary : null,
    route_score: typeof r.route_score === "number" ? r.route_score : null,
  };
}

function normEntry(raw: unknown): RouteEntry | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.fixture_id !== "string" || typeof r.market !== "string" || typeof r.selection !== "string") return null;
  const alts: RouteRecommendation[] = Array.isArray(r.alternatives)
    ? r.alternatives.map(normRecommendation).filter((x): x is RouteRecommendation => x !== null)
    : [];
  return {
    fixture_id: r.fixture_id,
    market: r.market,
    selection: r.selection,
    recommended: normRecommendation(r.recommended),
    alternatives: alts,
    considered_count: typeof r.considered_count === "number" ? r.considered_count : 0,
  };
}

export function useSlipRouting(
  legs: RouteLegInput[] | null,
  region: string = "GH",
): SlipRoutingState {
  const { user } = useAuthSession();

  const stableLegs = useMemo(() => (legs && legs.length > 0 ? legs : null), [legs]);

  // db/types.ts hasn't been regenerated since the route_slip RPC
  // landed in Phase 2.
  const args = useMemo<Record<string, unknown> | undefined>(
    () =>
      stableLegs
        ? {
            p_legs: stableLegs.map((l) => ({
              fixture_id: l.fixture_id,
              market: l.market,
              selection: l.selection,
            })),
            p_user_id: user?.id ?? null,
            p_region: region,
          }
        : undefined,
    [stableLegs, user?.id, region],
  );

  const q = useRpcQuery<Record<string, unknown> | null>("route_slip", {
    args,
    enabled: Boolean(stableLegs),
  });

  const routes = useMemo<RouteEntry[]>(() => {
    const data = q.data;
    const rawRoutes = data && Array.isArray(data.routes) ? data.routes : [];
    return rawRoutes
      .map(normEntry)
      .filter((e): e is RouteEntry => e !== null);
  }, [q.data]);

  const lookup = useCallback(
    (fixtureId: string, market: string, selection: string): RouteEntry | null => {
      return (
        routes.find(
          (r) => r.fixture_id === fixtureId && r.market === market && r.selection === selection,
        ) ?? null
      );
    },
    [routes],
  );

  return useMemo<SlipRoutingState>(
    () => ({
      routes,
      loading: q.isLoading,
      error: q.error ? q.error.message : null,
      lookup,
      refetch: async () => {
        await q.refetch();
      },
    }),
    [routes, q.isLoading, q.error, lookup, q.refetch],
  );
}
